import React, { useState, useEffect, useMemo, useRef } from "react";
import Select from "react-select";
import {
  getSelectPlaceholder,
  getSelectOptions,
} from "../../../utils/selectHelpers";

const getOptionIndex = (options, value) => {
  if (!options || !Array.isArray(options)) {
    return -1;
  }
  for (let i = 0; i < options.length; i++) {
    const keyValueMap = options[i];
    if (keyValueMap && keyValueMap["value"] === value) {
      return i;
    }
  }
  return -1;
};

const getInitialSelectedOption = (options, index) => {
  if (
    index === -1 ||
    !options ||
    !Array.isArray(options) ||
    index >= options.length
  ) {
    return null;
  }
  return options[index];
};

export default function SingleSelect({
  optionParam,
  name,
  allowOptions,
  defaultValue,
  onChange,
  isDisabled,
  placeholder,
  formMethods,
  fieldName,
  onBlur: customOnBlur,
}) {
  const options = useMemo(
    () => getSelectOptions(optionParam, allowOptions) || [],
    [optionParam, allowOptions]
  );
  const populatedValueIndex = useMemo(
    () => getOptionIndex(options, defaultValue),
    [options, defaultValue]
  );

  // Calculate selected option directly from props
  const [selectedOption, setSelectedOption] = useState(
    getInitialSelectedOption(options, populatedValueIndex)
  );

  const hasRegistered = useRef(false);

  // Register field with formMethods for validation
  useEffect(() => {
    if (formMethods && fieldName && !hasRegistered.current) {
      formMethods.register(fieldName, {
        required: "State is required",
      });
      hasRegistered.current = true;
    }
  }, [formMethods, fieldName]);

  // Sync with formMethods when defaultValue changes, but do not overwrite
  // an existing non-empty value in RHF
  useEffect(() => {
    if (formMethods && fieldName && defaultValue !== undefined) {
      const current = formMethods.getValues(fieldName);
      const hasCurrent =
        current !== undefined && current !== null && String(current) !== "";
      const isMeaningfulDefault =
        defaultValue !== null && String(defaultValue) !== "";
      if (!hasCurrent && isMeaningfulDefault) {
        formMethods.setValue(fieldName, defaultValue, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
    }
  }, [defaultValue, formMethods, fieldName]);

  // Sync selected option with react-hook-form value changes (including programmatic setValue)
  useEffect(() => {
    if (formMethods && fieldName) {
      const subscription = formMethods.watch((allValues) => {
        const currentValue = allValues?.[fieldName];
        if (currentValue === undefined) return;
        if (currentValue === null || String(currentValue) === "") {
          setSelectedOption(null);
          return;
        }
        const valueIndex = getOptionIndex(options, currentValue);
        const newSelectedOption = getInitialSelectedOption(options, valueIndex);
        if (newSelectedOption) {
          setSelectedOption(newSelectedOption);
        }
      });

      // Initial sync
      const currentValue = formMethods.getValues(fieldName);
      if (
        currentValue !== undefined &&
        currentValue !== null &&
        String(currentValue) !== ""
      ) {
        const valueIndex = getOptionIndex(options, currentValue);
        const newSelectedOption = getInitialSelectedOption(options, valueIndex);
        if (newSelectedOption) {
          setSelectedOption(newSelectedOption);
        }
      }

      return () => subscription.unsubscribe();
    }
  }, [formMethods, fieldName, options]);

  const handleChange = (selectedOption) => {
    setSelectedOption(selectedOption);

    const value = selectedOption ? selectedOption.value : null;

    // Call the original onChange if provided
    if (onChange) {
      onChange(value);
    }

    // Update react-hook-form if available
    if (formMethods && fieldName) {
      formMethods.setValue(fieldName, value, {
        shouldValidate: false, // Don't validate on setValue to avoid global validation
        shouldDirty: true,
      });

      // Validate this specific field immediately to clear errors when valid value is selected
      // Use requestAnimationFrame to ensure the setValue has completed
      requestAnimationFrame(() => {
        formMethods.trigger(fieldName);
      });
    }
  };

  const handleBlur = () => {
    // Call custom onBlur if provided (for address modal validation)
    if (customOnBlur) {
      customOnBlur();
    }

    // Trigger validation on blur to show error messages for this specific field only
    // This will show errors if field is invalid, or clear errors if field is valid
    if (formMethods && fieldName) {
      formMethods.trigger(fieldName);
    }
  };

  return (
    <div className="select-wrapper">
      <Select
        options={options}
        name={name}
        value={selectedOption}
        onChange={handleChange}
        onBlur={handleBlur}
        required
        isDisabled={isDisabled}
        placeholder={getSelectPlaceholder(optionParam, placeholder)}
        className="react-select-container"
        classNamePrefix="react-select"
      />
    </div>
  );
}
